import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { differenceInMilliseconds } from 'date-fns';
import { useAtomValue, useSetAtom } from 'jotai';
import { capitalize, startCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import type { BoundingBox, RBLPoint, RBLSideLine } from '../@types/global';
import { DataSource, RBLSide, RBLType } from '../@types/global';
import type {
  GetPatientLatestImagesResponse,
  LatestImage,
  TUserValidation,
} from '../@types/latestImages';
import type {
  GetPatientLatestImagesResponseV2,
  ImageAnalyses,
  LatestImagesTeethV2,
} from '../@types/latestImagesV2';
import { selectedVisitAtom } from '../app/appHeader/imageViewerMenu/atoms';
import { updateUserName } from '../modules/annotator/AnnotatorActions';
import {
  fmxVersionAtom,
  imageQualityAtom,
  latestImagesVisitDateAtom,
} from '../modules/annotator/atoms';
import { getPatientName } from '../modules/uploader/patientList/patientListTableContainer/utils';
import { mixpanelEvents, useTrackEvent } from '../utils/mixpanel';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';
import {
  extractBoundingBoxFromGeoJSON,
  useLatestImages,
} from './useLatestImages';
import { usePreferences } from './usePreferences';

interface UseLatestImagesV2ToV1Params {
  videaPatientId: string;
  externalPatientId?: string;
  fmxVersion?: 1 | 2;
  limit?: number;
  estimatedVisitId?: string | null;
  isInsuranceExport?: boolean;
  options?: UseQueryOptions<
    GetPatientLatestImagesResponse,
    unknown,
    GetPatientLatestImagesResponse
  >;
  returnAllFmxSorts?: boolean;
}

interface UseLatestImagesV2Params {
  videaPatientId: string;
  limit?: number;
  estimatedVisitId?: string | null;
  returnAllFmxSorts?: boolean;
  options?: UseQueryOptions<
    GetPatientLatestImagesResponseV2,
    unknown,
    GetPatientLatestImagesResponseV2
  >;
}

const analysesTeethToCaries = (teeth: LatestImagesTeethV2[]) => {
  return teeth.flatMap(
    (tooth) =>
      tooth.caries?.map((caries) => ({
        ...caries,
        ...(extractBoundingBoxFromGeoJSON(caries.bounds) as BoundingBox),
        confidenceScore: caries.confidenceScore ?? undefined,
        toothNumber: tooth.toothId,
        toothIdentifier: tooth.toothId,
        userValidation: caries.userValidation
          ? (startCase(caries.userValidation) as TUserValidation)
          : undefined,
      })) || [],
  );
};

const analysesTeethToCalculus = (teeth: LatestImagesTeethV2[]) => {
  return teeth.flatMap(
    (tooth) =>
      tooth.calculus?.map((calculus) => ({
        ...calculus,
        ...(extractBoundingBoxFromGeoJSON(calculus.bounds) as BoundingBox),
        confidenceScore: calculus.confidenceScore ?? undefined,
        userValidation: calculus.userValidation
          ? (startCase(calculus.userValidation) as TUserValidation)
          : undefined,
        toothNumber: tooth.toothId,
      })) || [],
  );
};

const analysesTeethToPRL = (teeth: LatestImagesTeethV2[]) => {
  return teeth.flatMap(
    (tooth) =>
      tooth.periapicalRadiolucency?.map((periapicalRadiolucency) => ({
        ...periapicalRadiolucency,
        ...(extractBoundingBoxFromGeoJSON(
          periapicalRadiolucency.bounds,
        ) as BoundingBox),
        toothNumber: tooth.toothId,
        userValidation: periapicalRadiolucency.userValidation
          ? (startCase(
              periapicalRadiolucency.userValidation,
            ) as TUserValidation)
          : undefined,
      })) || [],
  );
};

const analysesTeethToRAL = (teeth: LatestImagesTeethV2[]) => {
  return teeth.flatMap(
    (tooth) =>
      tooth.restorativeAdjacentLucency?.map((restorativeAdjacentLucency) => ({
        ...restorativeAdjacentLucency,
        ...(extractBoundingBoxFromGeoJSON(
          restorativeAdjacentLucency.bounds,
        ) as BoundingBox),
        toothNumber: tooth.toothId,
        userValidation: restorativeAdjacentLucency.userValidation
          ? (startCase(
              restorativeAdjacentLucency.userValidation,
            ) as TUserValidation)
          : undefined,
      })) || [],
  );
};

const analysesTeethToRBL = (
  teeth: LatestImagesTeethV2[],
  imageType: 'PERIAPICAL' | 'BITEWING' | 'PANORAMIC' | 'COLOR_IMAGE' | null,
) => {
  return teeth.flatMap((tooth) =>
    tooth?.boneLevels.map((boneLevel) => {
      const mesialSidePoints: RBLPoint[] = [];
      if (boneLevel.cejMesial && boneLevel.cejMesial.coordinates.length >= 2) {
        mesialSidePoints.push({
          id: boneLevel.id + '.cejMesial',
          rblType: RBLType.cej,
          x: boneLevel.cejMesial.coordinates[0],
          y: boneLevel.cejMesial.coordinates[1],
        });
      }
      if (boneLevel.ablMesial && boneLevel.ablMesial.coordinates.length >= 2) {
        mesialSidePoints.push({
          id: boneLevel.id + '.ablMesial',
          rblType: RBLType.abd,
          x: boneLevel.ablMesial.coordinates[0],
          y: boneLevel.ablMesial.coordinates[1],
        });
      }
      if (
        boneLevel.mesialRoot &&
        boneLevel.mesialRoot.coordinates.length >= 2
      ) {
        mesialSidePoints.push({
          id: boneLevel.id + '.mesialRoot',
          rblType: RBLType.root,
          x: boneLevel.mesialRoot.coordinates[0],
          y: boneLevel.mesialRoot.coordinates[1],
        });
      }

      const distalSidePoints: RBLPoint[] = [];
      if (boneLevel.cejDistal && boneLevel.cejDistal.coordinates.length >= 2) {
        distalSidePoints.push({
          id: boneLevel.id + '.cejDistal',
          rblType: RBLType.cej,
          x: boneLevel.cejDistal?.coordinates[0],
          y: boneLevel.cejDistal?.coordinates[1],
        });
      }
      if (boneLevel.ablDistal && boneLevel.ablDistal.coordinates.length >= 2) {
        distalSidePoints.push({
          id: boneLevel.id + '.ablDistal',
          rblType: RBLType.abd,
          x: boneLevel.ablDistal.coordinates[0],
          y: boneLevel.ablDistal.coordinates[1],
        });
      }
      if (
        boneLevel.distalRoot &&
        boneLevel.distalRoot.coordinates.length >= 2
      ) {
        distalSidePoints.push({
          id: boneLevel.id + '.distalRoot',
          rblType: RBLType.root,
          x: boneLevel.distalRoot.coordinates[0],
          y: boneLevel.distalRoot.coordinates[1],
        });
      }
      let distanceMMMesial = null;
      if (boneLevel.mesialDistance) {
        distanceMMMesial =
          imageType === 'PERIAPICAL'
            ? Number((boneLevel.mesialDistance * 100).toFixed(1))
            : Number(boneLevel.mesialDistance.toFixed(1));
      }

      const mesialSide: RBLSideLine = {
        side: RBLSide.mesial,
        distanceMM: distanceMMMesial,
        points: mesialSidePoints,

        userValidation: boneLevel.userValidationMesial
          ? (capitalize(boneLevel.userValidationMesial) as TUserValidation)
          : undefined,
      };
      let distanceMMDistal = null;
      if (boneLevel.distalDistance) {
        distanceMMDistal =
          imageType === 'PERIAPICAL'
            ? Number((boneLevel.distalDistance * 100).toFixed(1))
            : Number(boneLevel.distalDistance.toFixed(1));
      }

      const distalSide: RBLSideLine = {
        side: RBLSide.distal,
        distanceMM: distanceMMDistal,
        points: distalSidePoints,
        userValidation: boneLevel.userValidationDistal
          ? (capitalize(boneLevel.userValidationDistal) as TUserValidation)
          : undefined,
      };
      const rblFinding = {
        sides: [mesialSide, distalSide],
        toothNumber: tooth.toothId,
        toothIdentifier: tooth.toothId,
      };
      return rblFinding;
    }),
  );
};

const analysesTeethToToothIdentification = (teeth: LatestImagesTeethV2[]) => {
  return teeth.flatMap((tooth) => ({
    toothNumber: tooth.toothId,
    id: tooth.toothId,
    xMin: 0,
    xMax: 0,
    yMin: 0,
    yMax: 0,
  }));
};

export const analysesToAnalyzedImages = (analyses: ImageAnalyses[]) => {
  const analyzedImages =
    analyses.map((analysis) => {
      const cariesFindings = analysesTeethToCaries(analysis.teeth);
      const calculusFindings = analysesTeethToCalculus(analysis.teeth);
      const prlFindings = analysesTeethToPRL(analysis.teeth);
      const ralFindings = analysesTeethToRAL(analysis.teeth);
      const rblFindings = analysesTeethToRBL(
        analysis.teeth,
        analysis.imageType,
      );
      const toothIdentificationFindings = analysesTeethToToothIdentification(
        analysis.teeth,
      );

      const latestImage: LatestImage = {
        analysisState: 'COMPLETE', // missing
        detectedImageType: analysis.imageType,
        imageId: analysis.mongoImageId || String(analysis.imageId),
        imageManifestId: analysis.imageId,
        fmxIndex:
          typeof analysis.fmxIndex === 'number' && analysis.fmxIndex >= 0
            ? analysis.fmxIndex
            : null,
        fmxPriority: analysis.fmxPriority,
        bwViewIndex: analysis.bwViewIndex,
        bwViewPriority: analysis.bwViewPriority,
        videaErrorCode: null, // missing
        findings: {}, // unnecessary, use models -> findings
        imageDimension: {
          heightPx: analysis.heightPx || 0,
          widthPx: analysis.widthPx || 0,
        },
        imageGroup:
          typeof analysis.fmxIndex === 'number' && analysis.fmxIndex >= 0
            ? 'FMX'
            : 'OTHER',
        // imageGroup: analysis.imageGroup || 'OTHER',
        imageKeys: {
          original: { key: analysis.originalPath },
          s3path: analysis.path,
          sharpened: { key: analysis.sharpenedPath },
        },
        isCached: false, // missing
        models: {
          calculus: { findings: calculusFindings },
          caries: { findings: cariesFindings },
          periapical_radiolucency: { findings: prlFindings },
          radiographic_boneloss: { findings: rblFindings },
          tooth_identification: {
            findings: toothIdentificationFindings,
          },
          restorative_adjacent_lucency: { findings: ralFindings },
        },
        updatedAt: analysis.updatedAt,
        createdAt: analysis.createdAt,
        imageAcquiredAt: analysis.imageAcquiredAt || undefined,
        originalPath: analysis.originalPath,
        path: analysis.path,
        sharpenedPath: analysis.sharpenedPath,
        sharpenedHD2Path: analysis.sharpenedHD2Path,
        userModifications: analysis.userModifications,
        userAnnotations: analysis.userAnnotations?.map((ua) => ({
          ...ua,
          ...(extractBoundingBoxFromGeoJSON(ua.bounds) as BoundingBox),
          userValidation: ua.userValidation
            ? (startCase(ua.userValidation) as TUserValidation)
            : undefined,
        })),
        thumbnailLargePath: analysis.thumbnailLargePath,
        thumbnailMediumPath: analysis.thumbnailMediumPath,
        thumbnailSmallPath: analysis.thumbnailSmallPath,
        modelHashes: analysis.modelHashes || undefined,
        photoView: analysis.photoView,
        teethMeetingSrpCriteria: analysis.teeth
          .filter((t) => t.meetsSrpCriteria)
          .map((t) => t.toothId),
        teethMatchingTreatment: analysis.teeth
          .filter((t) => t.matchesTreatment)
          .map((t) => t.toothId),
      };
      return latestImage;
    }) || [];

  return analyzedImages;
};

export const analysesToLatestImage = (
  resp: GetPatientLatestImagesResponseV2,
) => {
  const analyzedImages: LatestImage[] = resp.analyses
    ? analysesToAnalyzedImages(resp.analyses)
    : [];

  const latestImagesV1Response: GetPatientLatestImagesResponse = {
    usingLatestModel: resp.usingLatestModel || false,
    estimatedVisitId: resp.estimatedVisitId || -1,
    limit: String(resp.limit),
    total: resp.total,
    dob: resp.dob,
    imageCreatedAt: resp.imageCreatedAt,
    patientName: resp.patientName,
    analyzedImages: analyzedImages,
    analyses: resp.analyses,
    fmxIndexesByType: resp.fmxIndexesByType,
  };
  return latestImagesV1Response;
};

export const useLatestImagesV2ToV1 = ({
  videaPatientId,
  externalPatientId,
  limit = 50,
  estimatedVisitId,
  options,
  isInsuranceExport,
  returnAllFmxSorts = false,
}: UseLatestImagesV2ToV1Params) => {
  const { data: preferences, isLoading: isPreferencesLoading } =
    usePreferences();
  const { trackEvent } = useTrackEvent();
  const selectedVisit = useAtomValue(selectedVisitAtom);
  // If a visit is passed in, use it. Otherwise the atom will be the default value.
  const visitId = estimatedVisitId || selectedVisit;
  const fmxVersion = useAtomValue(fmxVersionAtom);
  const setLatestImagesVisitDateAtom = useSetAtom(latestImagesVisitDateAtom);
  const setImageQuality = useSetAtom(imageQualityAtom);
  const user = useSelector((state: any) => state?.login?.user);
  const dispatch = useDispatch();
  const isLatestImagesV2Enabled = !!preferences?.enableLatestImagesV2;
  const isPreferencesLoaded =
    !!preferences &&
    Object.keys(preferences).length > 0 &&
    !isPreferencesLoading;

  const latestImagesV1 = useLatestImages({
    videaPatientId,
    externalPatientId,
    limit,
    estimatedVisitId,
    options: {
      ...options,
      enabled:
        isPreferencesLoaded &&
        !isLatestImagesV2Enabled &&
        options?.enabled !== false,
    },
    isInsuranceExport,
  });

  const displayDentalPhotos = preferences?.displayDentalPhotos;

  const path = `${endpoints.v2}/${endpoints.patient}/${videaPatientId}/${endpoints.latestImages}`;

  const latestImagesV2 = useQuery<GetPatientLatestImagesResponse>(
    [
      endpoints.patient,
      endpoints.latestImages,
      'v2',
      videaPatientId,
      visitId,
      {
        bwOnly: false,
        fmxVersion,
        returnAllFmxSorts,
      },
      isInsuranceExport,
    ],
    async () => {
      const priorTime = new Date();
      return makeAPIRequestReactQuery<GetPatientLatestImagesResponseV2>(
        path,
        {
          bwOnly: false,
          limit,
          fmxVersion,
          estimatedVisitId: visitId,
          returnAllFmxSorts,
        },
        'GET',
        { headers: { practiceId: user?.primaryPracticeId } },
      ).then((resp) => {
        trackEvent(mixpanelEvents.latestImagesResponseTime, {
          loadTime: differenceInMilliseconds(new Date(), priorTime),
          isV2: true,
          patientId: videaPatientId,
          estimatedVisitId: visitId,
          analysesCached: resp.analysesCached,
          imageUrlsCached: resp.imageUrlsCached,
        });

        setImageQuality(resp.imageQuality);

        return analysesToLatestImage(resp);
      });
    },
    {
      ...options,
      enabled:
        isLatestImagesV2Enabled &&
        !!visitId &&
        !!videaPatientId &&
        options?.enabled !== false,
      onSuccess: (data) => {
        let filteredData = data;
        let images = data.analyzedImages;
        if (displayDentalPhotos !== 'on') {
          images = data.analyzedImages?.filter(
            (i) => i.detectedImageType !== 'COLOR_IMAGE',
          );
        }
        filteredData = {
          ...data,
          analyzedImages: images,
        };
        if (options?.onSuccess) {
          options.onSuccess(filteredData);
        }
        if (filteredData.analyzedImages[0]?.imageAcquiredAt) {
          setLatestImagesVisitDateAtom(
            new Date(filteredData.analyzedImages[0].imageAcquiredAt),
          );
        }
        if (filteredData.patientName) {
          dispatch(
            updateUserName(
              videaPatientId,
              getPatientName(filteredData.patientName, 0, true),
            ),
          );
        }
      },
      select: (latestImagesResponse: GetPatientLatestImagesResponse) => ({
        ...latestImagesResponse,
        analyzedImages: latestImagesResponse.analyzedImages
          .filter(
            (i) =>
              displayDentalPhotos === 'on' ||
              i.detectedImageType !== 'COLOR_IMAGE',
          )
          .map((image) => ({
            ...image,
            // the old workflow has a few complex functions needing this, and the PPD workflow does not
            // Add this dummy prop for now until the old workflow is removed
            path: image.path,
            patientInfo: {
              patientId: videaPatientId,
              externalPatientId: externalPatientId ?? null,
            },
            // detect uses objectId as a image id in the old VCA workflow.
            // Instead of changing that in multiple places just set the prop here
            objectId: image.imageId,
            dataSource: DataSource.NUCLEUS,
          })),
      }),
    },
  );
  if (!isLatestImagesV2Enabled) {
    return latestImagesV1;
  }
  return latestImagesV2;
};

export const useLatestImagesV2 = ({
  videaPatientId,
  limit = 50,
  estimatedVisitId,
  options,
  returnAllFmxSorts = false,
}: UseLatestImagesV2Params) => {
  const { trackEvent } = useTrackEvent();
  const selectedVisit = useAtomValue(selectedVisitAtom);
  // If a visit is passed in, use it. Otherwise the atom will be the default value.
  const visitId = estimatedVisitId || selectedVisit;
  const fmxVersion = useAtomValue(fmxVersionAtom);
  const user = useSelector((state: any) => state?.login?.user);
  const setImageQuality = useSetAtom(imageQualityAtom);

  const displayDentalPhotos = usePreferences()?.data?.displayDentalPhotos;

  const path = `v2/${endpoints.patient}/${videaPatientId}/${endpoints.latestImages}`;

  return useQuery<GetPatientLatestImagesResponseV2>(
    [
      endpoints.patient,
      endpoints.latestImages,
      videaPatientId,
      visitId,
      {
        bwOnly: false,
        fmxVersion,
        returnAllFmxSorts,
      },
    ],
    async () => {
      const priorTime = new Date();
      return makeAPIRequestReactQuery<GetPatientLatestImagesResponseV2>(
        path,
        {
          bwOnly: false,
          limit,
          fmxVersion,
          estimatedVisitId: visitId,
          returnAllFmxSorts,
        },
        'GET',
        { headers: { practiceid: user?.primaryPracticeId } },
      ).then((resp) => {
        trackEvent(mixpanelEvents.latestImagesResponseTime, {
          loadTime: differenceInMilliseconds(new Date(), priorTime),
          isV2: true,
          patientId: videaPatientId,
          estimatedVisitId: visitId,
          analysesCached: resp.analysesCached,
          imageUrlsCached: resp.imageUrlsCached,
        });
        setImageQuality(resp.imageQuality);
        return resp;
      });
    },
    {
      ...options,
      select: (latestImagesResponse: GetPatientLatestImagesResponseV2) => ({
        ...latestImagesResponse,
        analyses: latestImagesResponse.analyses?.filter(
          (i) => displayDentalPhotos === 'on' || i.imageType !== 'COLOR_IMAGE',
        ),
      }),
      enabled: !!visitId && !!videaPatientId && options?.enabled !== false,
    },
  );
};
